<template>
  <b-table :data="entries" :loading="isLoading" :striped="true">
    <b-table-column field="when" label="Data si ora" v-slot="props">
      {{ props.row.when }}
    </b-table-column>
    <b-table-column field="user" label="Utilizator" v-slot="props">
      {{ props.row.user }}
    </b-table-column>
    <b-table-column field="template" label="Sablon" v-slot="props">
      {{ props.row.template }}
    </b-table-column>
    <b-table-column field="size" label="Marime" v-slot="props">
      {{ props.row.size }}
    </b-table-column>
    <b-table-column field="actions" label="" v-slot="props">
      <div class="buttons">
        <b-button
          size="is-small"
          icon-left="download"
          :loading="isDownloading(props.row.id)"
          :disabled="isBusy(props.row.id)"
          @click="download(props.row)"
        >
          Descarca
        </b-button>
        <b-button
          size="is-small"
          icon-left="shield-check-outline"
          :loading="isVerifying(props.row.id)"
          :disabled="isBusy(props.row.id)"
          @click="verify(props.row)"
        >
          Verifica
        </b-button>
      </div>
    </b-table-column>
    <template #empty>
      <p class="has-text-grey has-text-centered">
        {{ isLoading ? "Incarcare..." : "Nu exista documente emise." }}
      </p>
    </template>
  </b-table>
</template>

<script>
import { saveAs } from "file-saver";
import SuccessionService from "@/services/succession.service";
import { notify } from "@/helpers/notify";
import { describeError } from "@/helpers/resdpsErrors";
import { formatDateTime, fromApiUtc, userLabel } from "@/helpers/resdps";

// Marimea in KB, rotunjita; un fisier nevid primeste minim 1 KB, ca sa nu apara "0 KB" pentru un
// document care exista de fapt.
const formatSize = (bytes) => {
  if (!bytes || bytes <= 0) return "0 KB";
  return Math.max(1, Math.round(bytes / 1024)) + " KB";
};

export default {
  name: "SuccessionFileDocuments",
  props: {
    fileId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      items: [],
      isLoading: false,
      // randul ocupat cu o operatie: cheia e id-ul documentului, valoarea "download" sau "verify".
      // Un rand ocupat isi blocheaza ambele butoane; celelalte randuri raman disponibile.
      busy: {},
    };
  },
  computed: {
    entries() {
      return this.items.map((item) => ({
        id: item.id,
        fileName: item.fileName,
        when: formatDateTime(fromApiUtc(item.generatedAtUtc)),
        user: userLabel(item.generatedByEmail, item.generatedBySub),
        template: item.templateCode + " v" + item.templateVersion,
        size: formatSize(item.sizeBytes),
      }));
    },
  },
  created() {
    this.load();
  },
  methods: {
    async load() {
      this.isLoading = true;
      try {
        this.items = await SuccessionService.listDocuments(this.fileId);
      } catch (error) {
        notify.error(this, describeError(error).message);
      } finally {
        this.isLoading = false;
      }
    },
    isBusy(id) {
      return !!this.busy[id];
    },
    isDownloading(id) {
      return this.busy[id] === "download";
    },
    isVerifying(id) {
      return this.busy[id] === "verify";
    },
    // Numele fisierului vine din randul afisat (elementul din lista), nu din antetul raspunsului.
    async download(row) {
      if (this.busy[row.id]) return undefined;
      this.$set(this.busy, row.id, "download");
      try {
        const downloaded = await SuccessionService.downloadDocument(
          this.fileId,
          row.id,
          row.fileName
        );
        saveAs(downloaded.blob, downloaded.name);
      } catch (error) {
        notify.error(this, describeError(error).message);
      } finally {
        this.$delete(this.busy, row.id);
      }
      return undefined;
    },
    async verify(row) {
      if (this.busy[row.id]) return undefined;
      this.$set(this.busy, row.id, "verify");
      try {
        const report = await SuccessionService.verifyDocument(
          this.fileId,
          row.id
        );
        if (report.intact) {
          notify.success(this, "Documentul si sablonul lui sunt neschimbate.");
        } else if (report.documentMatches === false) {
          notify.error(
            this,
            "Documentul a fost modificat dupa emitere. Anuntati administratorul."
          );
        } else if (report.templateMatches === false) {
          notify.error(
            this,
            "Sablonul din care a iesit documentul a fost modificat. Anuntati administratorul."
          );
        }
      } catch (error) {
        notify.error(this, describeError(error).message);
      } finally {
        this.$delete(this.busy, row.id);
      }
      return undefined;
    },
  },
};
</script>
